import React, { useCallback, useState, memo } from 'react';
import { SelectOption } from '@leasequery/lq.react-component-library/dist/models';
import {
  ReportCurrency,
  ReportType,
  ClientCustomCalendarModel,
} from '../../../../models';
import { CustomDateRange } from '../custom-date-range/CustomDateRange';
import {
  ReportTypeSetting,
  ReportSettingEndDateOption,
} from '../../../../models/reportTypeSetting';
import { buildDropdownList } from '../helpers/buildReportSettingData';
import ReportSettingPIT from '../ReportSettingPIT';
import { DateRangeMode } from '../date-range/DateRange';

interface ReportSettingCommitmentProps {
  onChange: (updatedValue: ReportSettingCommitmentState) => void;
  reportSettingState: ReportSettingCommitmentState;
  reportTypeSetting: ReportTypeSetting;
  isAllowPIT: boolean;
  customCalendarSettings?: ClientCustomCalendarModel;
}

export interface ReportSettingCommitmentState {
  selectedCurrency: SelectOption<ReportCurrency>;
  shortStartDate: string;
  shortEndDate: string;
  selectedRunBy: SelectOption<string>;
  selectedEndDateOption: ReportSettingEndDateOption;
  isFixedPaymentOnly: boolean;
  isValid: boolean;
}

function ReportDateSettingCommitment({
  onChange,
  reportSettingState,
  reportTypeSetting,
  isAllowPIT,
  customCalendarSettings,
}: ReportSettingCommitmentProps): JSX.Element {
  const endDateList = buildDropdownList(
    'Commitment',
    'EndDate',
    reportTypeSetting,
    'CustomRadio',
  );
  const [isDateRangeValid, setDateRangeValidStatus] = useState(false);
  const inputHandler = useCallback(
    (
      selectedCurrency: SelectOption<ReportCurrency>,
      shortStartDate: string,
      shortEndDate: string,
      selectedRunBy: SelectOption<string>,
      selectedEndDateOption: ReportSettingEndDateOption,
      isFixedPaymentOnly: boolean,
      isValid?: boolean,
    ) => {
      let isValidInput = isDateRangeValid;
      if (isValid !== undefined) {
        isValidInput = isValid;
        setDateRangeValidStatus(isValid);
      }

      onChange({
        ...reportSettingState,
        selectedCurrency: selectedCurrency,
        shortStartDate: shortStartDate,
        shortEndDate: shortEndDate,
        selectedRunBy: selectedRunBy,
        selectedEndDateOption: selectedEndDateOption,
        isFixedPaymentOnly: isFixedPaymentOnly,
        isValid: isValidInput,
      });
    },
    [isDateRangeValid, onChange, reportSettingState],
  );
  return (
    <>
      <CustomDateRange
        onChange={useCallback(
          (
            shortStartDate: string,
            shortEndDate: string,
            selectedEndDateOption: ReportSettingEndDateOption,
            isValid: boolean,
          ): void => {
            inputHandler(
              reportSettingState.selectedCurrency,
              shortStartDate,
              shortEndDate,
              reportSettingState.selectedRunBy,
              selectedEndDateOption,
              reportSettingState.isFixedPaymentOnly,
              isValid,
            );
          },
          [
            inputHandler,
            reportSettingState.selectedCurrency,
            reportSettingState.selectedRunBy,
            reportSettingState.isFixedPaymentOnly,
          ],
        )}
        shortStartDate={reportSettingState.shortStartDate}
        shortEndDate={reportSettingState.shortEndDate}
        selectedEndDateOption={reportSettingState.selectedEndDateOption}
        endDateList={endDateList}
        customCalendarPeriods={customCalendarSettings?.numberOfPeriods}
        endDateMode={DateRangeMode.FullDate}
      />
      <ReportSettingPIT
        reportType={ReportType.committment}
        reportSettingState={reportSettingState}
        reportTypeSetting={reportTypeSetting}
        customCalendarSettings={customCalendarSettings}
        isAllowPIT={isAllowPIT}
        onChange={useCallback(
          (state) => onChange(state as ReportSettingCommitmentState),
          [onChange],
        )}
      />
    </>
  );
}

export default memo(ReportDateSettingCommitment);
